import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, Stack, Chip, InputAdornment, Select, MenuItem, TextField, Autocomplete, Grid } from '@mui/material';
import api from '../services/api';
import { AreaChart } from '../components/charts/SimpleCharts';
import type { Product, Recipe } from '../types';
import { ProjectionChart } from '../components/charts/ProjectionChart';

export const AnalyticsPage: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [series, setSeries] = useState<{ week: string; value: number }[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [projectionWindow, setProjectionWindow] = useState<'1h'|'2h'|'4h'|'8h'|'day'>('day');
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [materialsMap, setMaterialsMap] = useState<Record<number, { currentStock: number; name: string }>>({});
  const [componentStocks, setComponentStocks] = useState<Record<string, number>>({});
  const [topSummary, setTopSummary] = useState<{ topSeller?: any; topMargin?: any } | null>(null);

  useEffect(() => {
    (async () => {
      const s = await api.getProductStats();
      setStats(s);
      // Load products (paginate if needed)
      try {
        const first = await api.getProducts({ page: 1, pageSize: 100 });
        let list = first.products || [];
        const totalPages = first.totalPages || 1;
        for (let p = 2; p <= Math.min(totalPages, 5); p++) {
          const res = await api.getProducts({ page: p, pageSize: 100 });
          list = list.concat(res.products || []);
        }
        setProducts(list);
        if (!selectedProductId && list.length > 0) setSelectedProductId(list[0].id);
      } catch {}
      try {
        const srs = await api.getSalesSeries(12);
        setSeries(srs.items || []);
      } catch {}
      try {
        const top = await api.getTopProducts({ days: 30, limit: 5 });
        setTopSummary({ topSeller: top.topSeller, topMargin: top.topMargin });
      } catch {}
    })();
  }, []);

  // Load materials snapshot for stock of M.P.
  useEffect(() => {
    (async () => {
      try {
        const mats = await api.getMaterials({ page: 1, pageSize: 500 });
        const mmap: Record<number, { currentStock: number; name: string }> = {};
        for (const m of (mats.materials || [])) mmap[m.id] = { currentStock: m.currentStock, name: m.name };
        setMaterialsMap(mmap);
      } catch {}
    })();
  }, []);

  // Load recipe and component stocks for selected product
  useEffect(() => {
    (async () => {
      if (!selectedProductId) { setSelectedRecipe(null); return; }
      try {
        const list = await api.getRecipes({ page: 1, pageSize: 1, productId: selectedProductId });
        const rec = list.recipes?.[0] || null;
        setSelectedRecipe(rec);
        // Preload component stocks (products only; materials come from materialsMap)
        const stocks: Record<string, number> = {};
        if (rec) {
          for (const it of (rec.items || [])) {
            if (it.componentProductId) {
              const key = `p:${it.componentProductId}`;
              try {
                const s = await api.getLocationStocks(it.componentProductId);
                stocks[key] = (s.items || []).reduce((acc, x) => acc + (x.stock || 0), 0);
              } catch { stocks[key] = 0; }
            }
          }
        }
        setComponentStocks(stocks);
      } catch { setSelectedRecipe(null); setComponentStocks({}); }
    })();
  }, [selectedProductId]);

  const totals = useMemo(() => {
    const total = stats?.totalProducts ?? products.length ?? 0;
    const active = stats?.activeProducts ?? products.filter(p => p.isActive).length ?? 0;
    const low = stats?.lowStockProducts ?? products.filter(p => (p.currentStock || 0) > 0 && (p.currentStock || 0) <= (p.reorderPoint || 0)).length ?? 0;
    const critical = stats?.criticalStockProducts ?? products.filter(p => (p.currentStock || 0) <= 0).length ?? 0;
    const atRisk = low + critical;
    const healthyPct = active > 0 ? Math.round(((active - atRisk) / Math.max(1, active)) * 100) : 0;
    return { total, active, low, critical, atRisk, healthyPct };
  }, [stats]);

  const dohStats = useMemo(() => {
    const doh = (products || []).map(p => p.daysOfStock).filter((v): v is number => typeof v === 'number' && isFinite(v));
    if (doh.length === 0) return null;
    const sorted = doh.slice().sort((a, b) => a - b);
    const q = (p: number) => {
      const idx = (sorted.length - 1) * p;
      const lo = Math.floor(idx);
      const hi = Math.ceil(idx);
      if (lo === hi) return sorted[lo];
      return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
    };
    const p95 = q(0.95);
    const p50 = q(0.5);
    const p5 = q(0.05);
    const avg = sorted.reduce((a, b) => a + b, 0) / sorted.length;
    return { p95, p50, p5, avg, count: sorted.length };
  }, [products]);

  const hoursForWindow = useMemo(() => ({ '1h': 1, '2h': 2, '4h': 4, '8h': 8, 'day': 24 })[projectionWindow], [projectionWindow]);

  // Compute replenishment inputs for ProjectionChart
  const projectionInputs = useMemo(() => {
    const p = products.find(x => x.id === selectedProductId);
    if (!p) return null;
    const rec = selectedRecipe;
    const rDay = p.daysOfStock > 0 ? (p.currentStock || 0) / Math.max(1e-6, p.daysOfStock) : 0; // units/day
    const rHour = rDay / 24;
    const baseLead = (rec?.items || []).reduce((max, it) => Math.max(max, it.procurementLeadDays ?? 7), 0) + 2; // + inbound buffer
    const serviceHours = (rec?.items || []).reduce((acc, it) => acc + ((it.componentServiceId && it.serviceProcessingHoursPerUnit) ? (it.serviceProcessingHoursPerUnit * (it.quantity || 0)) : 0), 0);
    const leadDays = baseLead + Math.ceil(((rec?.manufacturingTimeHours || 0) + serviceHours) / 8);
    // Buildable units from components
    let buildable = Infinity;
    if (rec && rec.items && rec.items.length > 0) {
      for (const it of rec.items) {
        const key = it.componentProductId ? `p:${it.componentProductId}` : `m:${it.componentMaterialId}`;
        const have = it.componentProductId ? (componentStocks[key] ?? 0) : (materialsMap[it.componentMaterialId || 0]?.currentStock ?? 0);
        const req = Math.max(1e-6, it.quantity || 0);
        buildable = Math.min(buildable, Math.floor(have / req));
      }
    } else {
      buildable = 0;
    }
    if (!isFinite(buildable)) buildable = 0;
    return { rHour, leadDays, buildable };
  }, [products, selectedProductId, selectedRecipe, componentStocks, materialsMap, hoursForWindow]);

  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>Análisis</Typography>

      {/* Destacados: más vendido y mayor margen */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Más vendido (30 días)</Typography>
              {topSummary?.topSeller ? (
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Stack>
                    <Typography variant="h6" fontWeight={600}>{topSummary.topSeller.productName}</Typography>
                    <Typography variant="caption" color="text.secondary">SKU {topSummary.topSeller.sku}</Typography>
                  </Stack>
                  <Stack alignItems="flex-end">
                    <Typography variant="h4">{topSummary.topSeller.units}</Typography>
                    <Typography variant="caption" color="text.secondary">unidades</Typography>
                  </Stack>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">Sin datos suficientes</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Mayor margen (30 días)</Typography>
              {topSummary?.topMargin ? (
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Stack>
                    <Typography variant="h6" fontWeight={600}>{topSummary.topMargin.productName}</Typography>
                    <Typography variant="caption" color="text.secondary">SKU {topSummary.topMargin.sku}</Typography>
                  </Stack>
                  <Stack alignItems="flex-end">
                    <Typography variant="h4">${Number(topSummary.topMargin.marginTotal || 0).toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary">margen bruto</Typography>
                  </Stack>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">Sin datos suficientes</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* KPIs priorizados (datos reales) */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Stock crítico</Typography>
              <Typography variant="h3" color="error.main" fontWeight={700}>{totals.critical}</Typography>
              <Typography variant="caption" color="text.secondary">Prioridad 1</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Stock bajo</Typography>
              <Typography variant="h3" color="warning.main" fontWeight={700}>{totals.low}</Typography>
              <Typography variant="caption" color="text.secondary">Prioridad 2</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Activos</Typography>
              <Typography variant="h4">{totals.active}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total</Typography>
              <Typography variant="h4">{totals.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Salud resumida + descripción */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Salud SS (aprox.)</Typography>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="h4" fontWeight={700}>{totals.healthyPct}%</Typography>
                <Typography variant="caption" color="text.secondary">SKUs con stock ≥ SS</Typography>
              </Stack>
              {dohStats && (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">DOH (distribución, {dohStats.count} SKUs)</Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip size="small" label={`p95: ${dohStats.p95.toFixed(1)} d`} />
                    <Chip size="small" label={`p50: ${dohStats.p50.toFixed(1)} d`} />
                    <Chip size="small" label={`p5: ${dohStats.p5.toFixed(1)} d`} />
                    <Chip size="small" label={`avg: ${dohStats.avg.toFixed(1)} d`} variant="outlined" />
                  </Stack>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Resumen</Typography>
              <Typography variant="body2" color="text.secondary">KPIs priorizados y DOH real. Abajo, tendencia de ventas (últimas 12 semanas).</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Tendencia de ventas (12 semanas)</Typography>
            <Box sx={{ width: '100%', height: 160 }}>
              <AreaChart values={series.map(s => s.value)} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Proyección de ventas vs stock */}
      <Box>
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>Proyección ventas vs stock</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Autocomplete
                  size="small"
                  options={products}
                  getOptionLabel={(p: any) => p?.name || ''}
                  isOptionEqualToValue={(o: any, v: any) => o?.id === v?.id}
                  value={(products || []).find(p => p.id === selectedProductId) || null}
                  onChange={(_, v) => setSelectedProductId(v ? (v as any).id : null)}
                  renderInput={(params) => <TextField {...params} label="Producto" fullWidth />}
                  sx={{ minWidth: 260 }}
                />
                <TextField
                  size="small"
                  label="Horizonte"
                  select
                  value={projectionWindow}
                  onChange={(e) => setProjectionWindow(e.target.value as any)}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="1h">1 hora</MenuItem>
                  <MenuItem value="2h">2 horas</MenuItem>
                  <MenuItem value="4h">4 horas</MenuItem>
                  <MenuItem value="8h">8 horas</MenuItem>
                  <MenuItem value="day">1 día</MenuItem>
                </TextField>
              </Stack>
            </Stack>
            {projectionInputs && selectedProductId ? (
              <ProjectionChart step={projectionWindow} horizonDays={14} stockStart={(products.find(x => x.id === selectedProductId)?.currentStock || 0)} buildableUnits={projectionInputs.buildable} salesRatePerHour={projectionInputs.rHour} leadDays={projectionInputs.leadDays} />
            ) : (
              <Typography variant="body2" color="text.secondary">Seleccioná un producto para ver la proyección.</Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
